import { useInput, multipleValidation } from '@libs/core';
import { ActionActionTypeEnum, StoreActionTypeEnum, TCommonInputOption, TextInputEnum, TInputData, TInputMethods, TUseAppHook, TWidget } from '@libs/types';
import { useCallback, useEffect, useRef, useState, ChangeEvent, FocusEvent } from 'react';

export type TConsumerInputMethods = TInputMethods & {
    setModal: TUseAppHook['setModal'];
};

const getClearValue = (val: string, widget: TWidget<TInputData, TCommonInputOption>): string => {
    const { options, id, alternativeId } = widget;
    if (val && (options?.inputType === 'number' || options?.inputType === 'decimal')) {
        return val.replace(/,/g, '.');
    }
    if (val && (id === TextInputEnum.ENTERPASSCODE || alternativeId === TextInputEnum.ENTERPASSCODE || options?.inputType === 'numericPassword')) {
        return val.replace(/\D/g, '');
    }
    return val;
};

/** Delay between the last keystroke and the validation that shows/hides the field error. */
export const INPUT_ERROR_DEBOUNCE_MS = 300;

type TValidationResult = ReturnType<typeof multipleValidation>;

type TDebouncedValidationParams = {
    mandatory?: boolean;
    regex?: string;
    validations?: TInputData['validations'];
    defaultErrorMessage?: string;
    /** Receives the validation result once the user has paused typing. */
    onResult: (result: TValidationResult) => void;
};

/**
 * Validates an input value after the user pauses typing, so the error shows while typing without flickering on
 * every keystroke. Each call restarts the delay; a pending check is dropped on unmount.
 * Shared by the text inputs (`useConsumerInput`) and the phone input, which stores its value itself and so
 * cannot go through `useConsumerInput`'s `handleChange`.
 *
 * @returns `validateWithDebounce(value)` — schedules the validation of `value`.
 */
export const useDebouncedValidation = ({ mandatory, regex, validations, defaultErrorMessage, onResult }: TDebouncedValidationParams) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        },
        [],
    );

    return useCallback(
        (value: string) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                onResult(multipleValidation(value, mandatory, regex, defaultErrorMessage, validations));
            }, INPUT_ERROR_DEBOUNCE_MS);
        },
        [defaultErrorMessage, mandatory, onResult, regex, validations],
    );
};

export const useConsumerInput = (widget: TWidget<TInputData, TCommonInputOption>, methods?: TConsumerInputMethods) => {
    const { triggerActionsByType, setModal } = methods ?? {};
    const { data, options = {} } = widget;
    const onBeforeSelect = useCallback(() => setModal?.(null), [setModal]);
    const {
        value,
        isFocused,
        isFilled,
        isError,
        isHidden,
        isDisabled,
        errorMessage,
        defaultErrorMessage,
        label,
        helperText,
        ref,
        setError,
        setErrorMessage,
        handleChange: coreHandleChange,
        handleBlur,
        handleFocus,
        handleClickDropdown,
        updateInputValue,
        systemMetadata,
    } = useInput(widget, methods, {
        parseValue: getClearValue,
        onBeforeSelect,
    });
    const { mandatory, regex, validations, submitLength } = data ?? {};
    const isFirstRenderRef = useRef<boolean>(true);
    const [failedValidationInternalId, setFailedValidationInternalId] = useState<string | null | undefined>(undefined);

    const applyValidationResult = useCallback(
        (validationResult: TValidationResult) => {
            setError(!validationResult.isValid);
            setErrorMessage(validationResult.errorMessage);
            setFailedValidationInternalId(validationResult.failedValidationInternalId ?? null);
        },
        [setError, setErrorMessage],
    );
    const validateWithDebounce = useDebouncedValidation({ mandatory, regex, validations, defaultErrorMessage, onResult: applyValidationResult });

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const nextValue = getClearValue(e?.target?.value, widget);
            validateWithDebounce(nextValue);
            coreHandleChange?.(e);

            const finalSubmitLength = submitLength ?? options?.maxLength;
            if (nextValue?.length === finalSubmitLength) {
                triggerActionsByType?.(ActionActionTypeEnum.onFilled)?.then();
            }
        },
        [coreHandleChange, options?.maxLength, submitLength, triggerActionsByType, validateWithDebounce, widget],
    );

    useEffect(() => {
        if (!isFirstRenderRef.current) {
            return;
        }
        isFirstRenderRef.current = false;
        if (value !== '' && isError !== true) {
            const validationResult = multipleValidation(value, mandatory, regex, defaultErrorMessage, validations);
            queueMicrotask(() => {
                setError(!validationResult.isValid);
                setErrorMessage(validationResult.errorMessage);
                setFailedValidationInternalId(validationResult.failedValidationInternalId ?? null);
            });
        }
    }, [defaultErrorMessage, isError, mandatory, regex, validations, value, setError, setErrorMessage, setFailedValidationInternalId]);

    useEffect(() => {
        if (systemMetadata.lastValueUpdateAction === StoreActionTypeEnum.updateWidgetValueByInternalId) {
            const validationResult = multipleValidation(value, mandatory, regex, defaultErrorMessage, validations);
            queueMicrotask(() => {
                setError(!validationResult.isValid);
                setFailedValidationInternalId(validationResult.failedValidationInternalId ?? null);
            });
        }
    }, [defaultErrorMessage, mandatory, regex, validations, value, systemMetadata.lastValueUpdateAction, setError, setFailedValidationInternalId]);

    return {
        value,
        isFocused,
        isFilled,
        isError,
        isHidden,
        isDisabled,
        errorMessage,
        defaultErrorMessage,
        label,
        helperText,
        ref,
        options,
        submitLength,
        mandatory,
        regex,
        validations,
        systemMetadata,
        handleBlur,
        handleFocus,
        handleClickDropdown,
        setError,
        setErrorMessage,
        handleChange,
        validateWithDebounce,
        updateInputValue,
        failedValidationInternalId,
        setFailedValidationInternalId,
    };
};

type TInputMask = typeof import('inputmask').default;

// https://robinherbots.github.io/Inputmask/
export const useInputMask = (
    regex?: string,
    placeholder?: string,
    mask?: string,
    showMaskOnFocus = true,
    focus = false,
    alias?: string,
    inputFormat?: string,
) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const inputMaskRef = useRef<TInputMask | null>(null);

    useEffect(() => {
        const input = inputRef?.current;
        let isMounted = true;

        if (input) {
            if (focus) {
                input.focus();
            }

            import(/* webpackChunkName: "inputmask" */ 'inputmask')
                .then((module) => {
                    if (!isMounted) {
                        return;
                    }
                    const Inputmask = (module.default || module) as unknown as TInputMask;
                    inputMaskRef.current = Inputmask;

                    Inputmask({
                        mask,
                        placeholder,
                        regex,
                        showMaskOnFocus,
                        alias,
                        inputFormat,
                    }).mask(input);
                    input.setAttribute('data-mask-ready', 'true');
                })
                .catch((error) => {
                    console.error('Error loading Inputmask', error);
                });
        }
        return function cleanup() {
            isMounted = false;
            if (input) {
                if (inputMaskRef.current) {
                    inputMaskRef.current.remove(input);
                }
                input.removeAttribute('data-mask-ready');
            }
        };
    }, [placeholder, regex, mask, showMaskOnFocus, focus, alias, inputFormat]);
    return inputRef;
};
export const useSimpleInput = (value: string) => {
    const [inputValue, setInputValue] = useState(value);
    const [isFocused, setIsFocused] = useState<boolean>(!!value);
    const [isFilled, setIsFilled] = useState<boolean>(!!value);
    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        const value = e?.target?.value;
        setIsFocused(false);
        setIsFilled(!!value);
    };

    const handleFocus = () => {
        setIsFilled(true);
        setIsFocused(true);
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInputValue(e?.target?.value);
    };
    return {
        handleChange,
        handleBlur,
        handleFocus,
        isFilled,
        isFocused,
        inputValue,
    };
};
